// ============================================================
// CLIMBING - what going up costs, and who it costs less.
//
// Deathwatch has a Climb skill (Strength-based, basic) and the app has
// always known each character's rating; nothing ever asked it about the
// map. Rising within a stride was free, so a staircase cost exactly what
// the same distance of flat floor did.
//
// No test is rolled. Training sets the RATE - the table's ruling - which
// keeps a drag instant and never interrupts a move to ask for a die.
//
// Pure. Zero React dependency.
// ============================================================
import type { SkillRating } from '../character/types';

/**
 * Metres of movement per metre of rise, by how well they climb.
 *
 * The rungs are cumulative on the sheet (`plus20 ⇒ plus10 ⇒ trained`),
 * so there are four, not two. Trained is DOUBLE - the rate the table
 * chose - and a master climbs as fast as they walk.
 */
export const CLIMB_RATES = { untrained: 3, trained: 2, plus10: 1.5, plus20: 1 } as const;

/** What a metre of rise costs this combatant. */
export function climbCostFor(climb: SkillRating | undefined, trainingCounts = true): number {
  if (!trainingCounts || !climb?.trained) return CLIMB_RATES.untrained;
  if (climb.plus20) return CLIMB_RATES.plus20;
  if (climb.plus10) return CLIMB_RATES.plus10;
  return CLIMB_RATES.trained;
}

/**
 * The metres of VERTICAL a step is charged for, from its signed rise.
 *
 * UP IS CHARGED WHOLE, from the first centimetre - a staircase is not
 * free, which was the whole point of v7.95.0.
 *
 * DOWN IS FREE WITHIN A STRIDE and charged whole beyond it. Stepping
 * off a kerb is not climbing; lowering yourself off a roof is, and it
 * was free at any depth until v8.7.0 - which meant the fastest way down
 * a tower was to walk off it, and that the affordability readout had
 * nothing to say about a descent because the answer was always yes.
 *
 * THE TWO ARE NOT QUITE MIRRORS, deliberately: a rise of 10 cm costs
 * and a drop of 10 cm does not. Charging every drop would bill a
 * combatant for walking down a gentle slope, and the free stride is
 * exactly the allowance `maxStep` already describes for going up - it
 * is simply the only half of it that was ever spent.
 */
export function climbCharge(rise: number, maxStep: number): number {
  if (!Number.isFinite(rise)) return 0;
  if (rise > 0) return rise;
  const drop = -rise;
  const stride = Number.isFinite(maxStep) ? Math.max(0, maxStep) : Infinity;
  return drop > stride ? drop : 0;
}

/** What this combatant is rated at, in the words the sheet uses. */
function rungName(climb: SkillRating | undefined, trainingCounts: boolean): string {
  if (!trainingCounts) return 'the Climb skill is not counted';
  if (!climb?.trained) return 'untrained';
  if (climb.plus20) return 'Climb +20';
  if (climb.plus10) return 'Climb +10';
  return 'Trained';
}

/** Metres, without a trailing `.0` on a whole number. */
const m = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/**
 * What the map is charging this combatant to go up and down, in words.
 *
 * HERE RATHER THAN IN THE PANEL because it is a sentence about a rule,
 * and rules live in the engine where they can be tested without a
 * browser. The panel renders it beside the line that already explains
 * why a reach is small.
 *
 * The stride figure is the mover's OWN `maxStep` - a third of their
 * height - so a Dreadnought's line reads 1.2 m and a guardsman's 0.6.
 * That is worth seeing: it is why one of them hops off what the other
 * has to climb down.
 */
export function climbRateNote(
  climb: SkillRating | undefined,
  o: { costs: boolean; trainingCounts: boolean; maxStep: number },
): string {
  const [first, ...rest] = climbRateFacts(climb, o);
  return `Climbing ${first} - ${rest.join(o.costs ? ' · ' : ', ')}`;
}

/**
 * The same thing in pieces, for the readout that shows it beside the
 * material facts - `Rockcrete · hardness 8 · blocks sight` and
 * `Climbing · 1.5 m per metre · Climb +10` are the same kind of
 * statement and are built the same way.
 */
export function climbRateFacts(
  climb: SkillRating | undefined,
  o: { costs: boolean; trainingCounts: boolean; maxStep: number },
): string[] {
  const stride = Number.isFinite(o.maxStep) ? `${m(o.maxStep)} m` : 'a stride';
  if (!o.costs) return ['is free', `a rise over ${stride} is refused`, 'a drop is not'];
  const rate = climbCostFor(climb, o.trainingCounts);
  return [`${m(rate)} m per metre`, rungName(climb, o.trainingCounts), `a drop over ${stride} costs the same`];
}
